import React, { useState, useEffect, useRef } from 'react';
import TripCard from '../components/mobile/Trip';
import TripHeader, { TripFilters } from '../components/common/Trip';
import TripForm from '../components/form/Trip';
import TripTable from '../components/desktop/Trip';
import Pagination from '../components/Pagination';

import { getDrivers } from '../services/drivers';
import { getTrips, updateStatus, downloadTripDocuments, downloadManifestPdf } from '../services/trips';
import { getVehicles } from '../services/vehicles';
import { useTripStatuses } from '../context/trips';
import { useOverallCount } from '../context/OverallCountContext';
import { USER_PROFILE } from '../services/auth';
import Spinner from '../components/ui/Spinner';
import { toast } from 'react-toastify';
import { useUnsavedChanges } from "../context/UnsavedChangesContext";


const Trips = () => {

  const profile = JSON.parse(localStorage.getItem(USER_PROFILE));

  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [trips, setTrips] = useState([]);
  const [count, setCount] = useState(0);
  const [editingTrip, setEditingTrip] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const confirmRef = useRef(null);
  const [filters, setFilters] = useState({
    type: '',
    branch: profile?.branch || '',
    requested_vehicle: '',
    assigned_vehicle: '',
    driver: '',
    status: '',
    customer: '',
    created_at: '',
    booking_date: '',
    expected_pickup_date: '',
    placement_pickup_date: '',
    actual_in_pickup_date: '',
    actual_out_pickup_date: '',
    expected_delivery_date: '',
    actual_in_delivery_date: '',
    actual_out_delivery_date: '',
  });
  const [inputSearch, setInputSearch] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const { confirmIfDirty } = useUnsavedChanges();


  const { tripStatuses: statuses, loading: trip_status_loading, refresh: refreshTripStatuses } = useTripStatuses({ reload: false, params: { status: 'active', paginate: false } });
  const { overallCount, loading: overall_count_loading, refresh: refreshOverallCount } = useOverallCount({ reload: false });

  const tripsPerPage = 20;

  useEffect(() => {
    setPendingPage(1);
  }, [search, filters]);

  useEffect(() => {
    if (pendingPage != null) {
      fetchTrips(pendingPage);
    }
    if (drivers.length == 0) {
      fetchDrivers();
    }
    if (vehicles.length == 0) {
      fetchVehicles();
    }
  }, [pendingPage]);

  const fetchTrips = async (page) => {
    setLoading(true);
    const params = {
      type: filters.type,
      branch: filters.branch > 0 ? filters.branch : undefined,
      requested_vehicle: filters.requested_vehicle,
      assigned_vehicle: filters.assigned_vehicle,
      driver: filters.driver > 0 ? filters.driver : undefined,
      status: filters.status,
      customer: filters.customer,
      created_at: filters.created_at,
      booking_date: filters.booking_date,
      expected_pickup_date: filters.expected_pickup_date,
      placement_pickup_date: filters.placement_pickup_date,
      actual_in_pickup_date: filters.actual_in_pickup_date,
      actual_out_pickup_date: filters.actual_out_pickup_date,
      expected_delivery_date: filters.expected_delivery_date,
      actual_in_delivery_date: filters.actual_in_delivery_date,
      actual_out_delivery_date: filters.actual_out_delivery_date,
      page, search
    };
    await getTrips(params)
      .then(([err, data]) => {
        setTrips(data.results);
        setCount(data.count);
        setCurrentPage(page);
        setPendingPage(null);
        setLoading(false);
        refreshOverallCount();
      })
      .catch((error) => {
        setPendingPage(null);
        setLoading(false);
      });
  };

  const fetchDrivers = async () => {
    setLoading(true);
    await getDrivers({paginate: false})
      .then(([err, data]) => {
        setDrivers(data || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const fetchVehicles = async () => {
    setLoading(true);
    await getVehicles({paginate: false})
      .then(([err, data]) => {
        setVehicles(data || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const handleAdd = () => {
    setEditingTrip(null);
    setShowFilters(false);
    setShowForm(true);
  };

  const handleEdit = (trip) => {
    setEditingTrip(trip);
    setShowFilters(false);
    setShowForm(true);
  };

  const handleSave = async (response) => {
    await fetchTrips(currentPage);
    if (!editingTrip) {
      toast.success("Trip Created Successfully.");
    } else {
      toast.success("Trip Updated Successfully.");
    }
    setShowForm(false);
  };

  const handleCancel = async () => {
    // setShowForm(false);
    confirmIfDirty(async () => {
      await fetchTrips(currentPage);
      setShowForm(false);
    });
  };

  const onSearch = (e) => {
    setInputSearch(e.target.value);
  };

  const onShowFilters = () => {
    setShowFilters(!showFilters);
  };

  const onFilterChange = (e, filterName) => {
    setFilters({ ...filters, [filterName]: e.target.value });
  };

  const handleTripSelection = (trip) => {
    handleEdit(trip);
  };

  const handleStateUpdate = async (trip, action, close_form = false) => {
    if (close_form) {
      setShowForm(false);
    }
    const [err, response] = await updateStatus(trip.id, action);
    if (err) {
      console.error("Error updating status: ", err);
      toast.error("Error Updating Status.");
    } else {
      toast.success("Status Updated Successfully.");
      await fetchTrips(currentPage);
    }
  };

  const handleDownloadDocs = async (trip) => {
    const [err] = await downloadTripDocuments(trip.id, trip.trip_code);
    if (err) {
      toast.error("No documents found for this trip.");
    }
  };

  const handleDownloadManifest = async (trip) => {
    const [err] = await downloadManifestPdf(trip.id, trip.trip_code);
    if (err) {
      toast.error(err.message || "Failed to download manifest.");
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(inputSearch.trim());
  };

  const onClearSearch = () => {
    setInputSearch('');
    setSearch('');
    setCurrentPage(1);
    setPendingPage(1);
  };

  return (
    <div className="p-4 w-full">
      <TripHeader
        inputSearch={inputSearch}
        count={count}
        onSearchSubmit={onSearchSubmit}
        showFilters={showFilters}
        onSearch={onSearch}
        onClearSearch={onClearSearch}
        onShowFilters={onShowFilters}
        handleAdd={handleAdd}
        trip={editingTrip}
        showForm={showForm}
        onBack={handleCancel}
        onStatusUpdate={handleStateUpdate}
        readOnly={["completed", "cancelled"].includes(editingTrip?.status)}
      />
      

      {/* Filters */}
      {showFilters && (
        <TripFilters
          filters={filters}
          onFilterChange={onFilterChange}
        />
      )}

      {/* Content Rendering */}
      {showForm ? (
        <TripForm 
          trip={editingTrip}
          onSave={handleSave}
          onCancel={handleCancel}
          readOnly={["completed", "cancelled"].includes(editingTrip?.status)}
        />
      ) : (
        <>
          {loading ? <Spinner /> : (
            <>
              <TripCard
                trips={trips}
                handleEdit={handleEdit}
                handleStateUpdate={handleStateUpdate}
                onDownloadDocs={handleDownloadDocs}
                onDownloadManifest={handleDownloadManifest}
              />

              <TripTable
                trips={trips}
                handleEdit={handleEdit}
                handleStateUpdate={handleStateUpdate}
                onTripSelected={handleTripSelection}
                onDownloadDocs={handleDownloadDocs}
                onDownloadManifest={handleDownloadManifest}
              />
            </>
          )}

          {/* Pagination */}
          <div className="fixed md:static bottom-0 left-0 w-full bg-white border-t shadow z-10">
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(count / tripsPerPage)}
              onPageChange={setPendingPage}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default Trips;
